import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { ConfirmDialog } from '../components/ConfirmDialog';

interface ConfirmOptions {
  title: string;
  message: ReactNode;
  description?: ReactNode;
  subtitle?: ReactNode | null;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface ConfirmContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const ConfirmDialogContext = createContext<ConfirmContextValue | null>(null);

type PendingState = (ConfirmOptions & { resolve: (v: boolean) => void }) | null;

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingState>(null);

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>(resolve => {
      setPending({ ...opts, resolve });
    });
  }, []);

  const handleConfirm = () => {
    if (!pending) return;
    pending.resolve(true);
    setPending(null);
  };

  const handleClose = () => {
    if (!pending) return;
    pending.resolve(false);
    setPending(null);
  };

  return (
    <ConfirmDialogContext.Provider value={{ confirm }}>
      {children}
      <ConfirmDialog
        isOpen={pending !== null}
        title={pending?.title ?? ''}
        message={pending?.message ?? ''}
        description={pending?.description}
        subtitle={pending?.subtitle}
        confirmLabel={pending?.confirmLabel ?? 'Potwierdź'}
        cancelLabel={pending?.cancelLabel ?? 'Anuluj'}
        danger={pending?.danger ?? true}
        onConfirm={handleConfirm}
        onClose={handleClose}
      />
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirmDialog() {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) throw new Error('useConfirmDialog must be used inside <ConfirmDialogProvider>');
  return ctx.confirm;
}
